// utils/qcApproval.js
// The "QC approved" button on the order screen: who may press it, when it is active, how the
// server's answers are read, and the copy of the approval that stays on the tablet so it is
// still shown after a reload or without a connection.

// Mirror of ROLES in server/auth.js (the server decides; this only decides what is shown).
// /api/auth/me returns the role name, not the number.
const ROLE_LEVELS = {
  Admin: 100,
  Supervisor: 80,
  Reviewer: 60,
  'R&D': 60,
  'R&D Reviewer': 60,
  Photographer: 20,
  'R&D Photographer': 20,
};
export const QC_MIN_ROLE_LEVEL = 80;

// The 6 core steps, in capture order: step_index 0-5 (server: CORE_STEP_COUNT).
export const CORE_STEP_COUNT = 6;

export function roleLevel(role) {
  return ROLE_LEVELS[role] || 0;
}

export function canApproveQc(user) {
  return roleLevel(user?.role) >= QC_MIN_ROLE_LEVEL;
}

// Indexes (0-5) of the core steps that have no photo on the server yet.
export function missingCoreSteps(photos) {
  const present = new Set((photos || []).map((p) => p.step_index));
  const missing = [];
  for (let i = 0; i < CORE_STEP_COUNT; i += 1) if (!present.has(i)) missing.push(i);
  return missing;
}

export function isQcDone(order) {
  return order?.qc_status === 'done';
}

// What the order screen shows: nothing, the approval status, or the button (active or not).
export function qcView({ user, order, photos }) {
  if (isQcDone(order)) return { mode: 'done' };
  if (!canApproveQc(user)) return { mode: 'hidden' };
  const missing = missingCoreSteps(photos);
  return { mode: 'button', enabled: missing.length === 0, missing };
}

// The 422 answer lists the missing steps; accept an index, a step_index/step_name object or a
// name, so a small change on the server does not turn the message into "undefined".
export function normalizeMissingSteps(list) {
  return (Array.isArray(list) ? list : []).map((step) => {
    const raw = step !== null && typeof step === 'object' ? (step.step_index ?? step.index ?? step.step_name ?? step.name) : step;
    const asNumber = typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : raw;
    return Number.isInteger(asNumber) ? { index: asNumber } : { name: String(raw) };
  });
}

// Reads a failed POST /api/orders/:id/qc-done into a message key (and the missing steps for 422).
export function qcErrorFrom(err) {
  const status = err?.response?.status;
  if (status === 409) return { key: 'qc.error.already' };
  if (status === 403) return { key: 'qc.error.forbidden' };
  if (status === 422) return { key: 'qc.error.missing', missing: normalizeMissingSteps(err.response.data?.missing_steps) };
  if (!err?.response) return { key: 'qc.error.network' };
  return { key: 'qc.error.generic' };
}

// qc_done_by may come back as the user id of the approver. For the person who just pressed the
// button we know the name; anything that is not their id is shown as it is.
export function approverName(qcDoneBy, user) {
  if (qcDoneBy && user && qcDoneBy === user.id) return user.full_name || '';
  return qcDoneBy || '';
}

// SQLite stamps ('YYYY-MM-DD HH:MM:SS', UTC) and ISO strings both end up here.
export function parseServerTime(value) {
  if (!value) return null;
  const text = String(value);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatQcTime(value, locale) {
  const date = parseServerTime(value);
  if (!date) return '';
  return date.toLocaleString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Copy on the tablet ──────────────────────────────────────────────────────────────────────
// The order answer is cached by the service worker, but that copy is from before the approval.
// The approval is therefore also kept here, per order, and laid over the order when it loads.
const STORE_KEY = 'photoapp_qc_done';
const STORE_MAX = 200;

function readStore() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORE_KEY));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberQcDone(orderId, { qc_done_at, qc_done_by }) {
  const store = readStore();
  store[orderId] = { qc_done_at, qc_done_by };
  const ids = Object.keys(store);
  ids.slice(0, Math.max(0, ids.length - STORE_MAX)).forEach((id) => delete store[id]);
  try { localStorage.setItem(STORE_KEY, JSON.stringify(store)); } catch {}
}

// The server's answer wins once it knows about the approval; until then (stale cache, or the
// order read while offline) the copy on the tablet fills it in.
export function withLocalQc(order) {
  if (!order || isQcDone(order)) return order;
  const local = readStore()[order.id];
  return local ? { ...order, qc_status: 'done', qc_done_at: local.qc_done_at, qc_done_by: local.qc_done_by } : order;
}
